<template>
  <div>
    <v-divider class="mt-4" />
    <div v-if="isAnimation && !noWmtsFeaturesFound && !mobileBreakpoints()">
      <v-row class="ma-0 align-end mt-4">
        <v-col
          cols="6"
          md="6"
          class="pa-0"
        >
          <div class="d-flex flex-column">
            <strong>{{ $t('ServiceCard.animation') }}</strong>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <b
                  style="font-size: 1rem;"
                  class="RMprimary-color"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ dailyData ? animationCurrentStep.split(" ")[0] : animationCurrentStep }}
                </b>
              </template>
              <span>{{ $t('ServiceCard.localTime') }}</span>
            </v-tooltip>
          </div>
        </v-col>
        <v-spacer />
        <v-col
          v-for="player of playerIcons"
          :key="player"
          cols="2"
          md="2"
          class="mobile-margin py-0"
        >
          <v-btn
            icon
            outlined
            color="#505050"
            :disabled="playerIconsDisabled(player) || isChangingStep"
            @click="changeAnimationStep(player)"
          >
            <v-icon dark>
              {{ player }}
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <div v-if="!isAnimation && !(noFeaturesFound || noWmtsFeaturesFound) && !mobileBreakpoints()">
      {{ $t('ServiceCard.lastUpdateDate') }} <b class="RMprimary-color">{{ dailyData ? animationCurrentStep.split(' ')[0] : animationCurrentStep }}</b>
    </div>
    <v-alert
      :value="noFeaturesFound || noWmtsFeaturesFound"
      dense
      type="warning"
      icon="mdi-alert-circle-outline"
      class="mb-0 mt-4"
      transition="scale-transition"
    >
      {{ noWmtsFeaturesFound ? $t('ServiceCard.noWmtsFeaturesFound') : $t('ServiceCard.errorAlert') }}
    </v-alert>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { getFormattedCurrentDate } from '@/utils.js';
import { BINARY_BASE_URL } from '@/constants.js';

export default {
  props: {
    serviceCardId: {
      type: String,
      default: ''
    },
    animationStepDateType: {
      type: String,
      default: ''
    },
    animationStep: {
      type: String,
      default: ''
    },
    layerType: {
      type: String,
      default: ''
    },
    serviceName: {
      type: String,
      default: ''
    },
    isAnimation: {
      type: Boolean,
      default: false
    },
    pauseAnimation: {
      type: Boolean,
      default: false
    },
    overlay: {
      type: Boolean,
      default: false
    },
    minStep: {
      type: Date,
      default: null
    },
    maxStep: {
      type: Date,
      default: null
    },
    selectedRasterLayer: {
      type: Object,
      default: null
    },
    selectedVectorLayer: {
      type: Object,
      default: null
    },
    animationLimitRangeArray: {
      type: Array,
      default: () => []
    },
    animationLimitRangeArrayPolicy: {
      type: Array,
      default: () => []
    },
    animationTimeArray: {
      type: Array,
      default: () => []
    },
    animationEmptySteps: {
      type: Array,
      default: () => []
    },
    animationStartIndex: {
      type: Number,
      default: 0
    },
    noFeaturesFound: {
      type: Boolean,
      default: false
    },
    noWmtsFeaturesFound: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isPlaying: false,
      isAnimationLoopRunning: false,
      isChangingStep: false,
      playerIcons: ['mdi-skip-backward', 'mdi-play', 'mdi-skip-forward'],
      index: this.animationStartIndex,
      animationCurrentStep: ''
    };
  },
  computed: {
    ...mapState(['map', 'policy', 'app_user', 'geoserver_data']),
    checkForActivatedLayers() {
      if (this.map.disableAnimationPlay) {
        return true;
      } else {
        return this.selectedVectorLayer !== null || this.selectedRasterLayer !== null;
      }
    },
    dailyData() {
      return this.animationStepDateType === 'dynamic_months' || this.animationStepDateType === 'dynamic_days' || (this.animationStepDateType === 'dynamic_hours' && this.animationStep >= 24);
    }
  },
  watch: {
    $route() {},
    pauseAnimation: {
      async handler(newValue) {
        if (newValue) {
          this.isPlaying = false;
        } else {
          if (!this.isPlaying) {
            this.isPlaying = true;
            await this.runAnimationLoop();
          }
        }
      }
    },
    animationTimeArray: {
      async handler(newValue) {
        if (Array.isArray(newValue) && newValue.length > 0) {
          if (!this.map.isAnimating.includes(this.serviceCardId)) {
            this.index = this.animationStartIndex;
          }

          await this.updateCurrentStep();
        }
      }
    }
  },
  mounted() {
    this.updateCurrentStep();
  },
  methods: {
    ...mapActions(['loadRasterLayer', 'updateVectorLayer', 'resetAnimationTime', 'getLayerExtent']),
    playerIconsDisabled(player) {
      if (this.animationTimeArray.length === 0) {
        return true;
      } else {
        return this.map.disableAnimationPlay && !(this.isPlaying && player === 'mdi-pause');
      }
    },
    getFormattedCurrentDateLocal(date) {
      return getFormattedCurrentDate(date);
    },
    async changeAnimationStep(player) {
      switch (player) {
        case 'mdi-skip-backward':
          this.isChangingStep = true;
          this.index = this.index - 1 < 0 ?
            this.animationTimeArray.length - 1 :
            this.index - 1;
          await this.loadLayer();
          await this.updateCurrentStep();
          this.isChangingStep = false;
          break;
        case 'mdi-skip-forward':
          this.isChangingStep = true;
          await this.nextAnimationStep();
          this.isChangingStep = false;
          break;
        case 'mdi-play':
          if (this.layerType === 'RASTER') {
            this.map.isAnimating.push(this.selectedRasterLayer.service_id);
          } else {
            this.map.isAnimating.push(this.selectedVectorLayer.service_id);
          }

          this.isPlaying = true;
          this.playerIcons[1] = 'mdi-pause';
          this.map.disableAnimationPlay = true;
          await this.runAnimationLoop();

          break;
        case 'mdi-pause':
          this.isPlaying = false;
          this.playerIcons[1] = 'mdi-play';
          this.map.disableAnimationPlay = false;
          await this.resetAnimationTime();
          break;
        default:
          break;
      }
    },
    async runAnimationLoop() {
      if (this.isAnimationLoopRunning) {
        return;
      }

      this.isAnimationLoopRunning = true;

      try {
        while (this.isPlaying && !this.pauseAnimation) {
          try {
            await this.nextAnimationStep(); // Carica il nuovo step
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Animation loop step failed, skipping frame', err);
          }

          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } finally {
        this.isAnimationLoopRunning = false;
      }
    },
    async nextAnimationStep() {
      if (this.pauseAnimation || !this.animationTimeArray.length) {
        return;
      }

      this.index += 1;

      if (this.index >= this.animationTimeArray.length) {
        this.index = 0;
      }

      await this.loadLayer();
      await this.updateCurrentStep();
    },
    async updateCurrentStep() {
      if (this.animationTimeArray[this.index]) {
        const vectorIndex = this.$route.params.id === 'Forecast' ? 0 : 1;
        const date = this.layerType === 'RASTER'
          ? new Date(this.animationTimeArray[this.index])
          : new Date(this.animationTimeArray[this.index]?.split('/')[vectorIndex]);

        this.animationCurrentStep = getFormattedCurrentDate(date);
      }
    },
    async loadLayer() {
      if (this.layerType === 'RASTER') {
        this.$emit('no-raster-features-found', this.animationEmptySteps.includes(this.animationTimeArray[this.index]));
        const extent = await this.getLayerExtent([this.selectedRasterLayer.ws_name, this.selectedRasterLayer.layer_name, this.selectedRasterLayer.layer_type, this.selectedRasterLayer.service_id]);
        const noData = this.animationEmptySteps.includes(this.animationTimeArray[this.index]);

        const rasterParams = {
          url: `${BINARY_BASE_URL}/fe_ogc/geoserver/wms`,
          params: {
            ENV: '',
            GROUP: this.$route.params.id,
            LAYERS: `${this.selectedRasterLayer.ws_name}:${this.selectedRasterLayer.layer_name}`,
            TIME: this.animationTimeArray[this.index],
            STYLES: this.selectedRasterLayer.layer_style,
            USERNAME: this.app_user.username,
            SERVICE_NAME: this.serviceName,
            FORMAT: 'image/vnd.jpeg-png8'
          },
          NO_DATA: noData,
          TYPE: 'RASTER',
          LAYER_SERVICE_ID: this.serviceCardId,
          UDM: this.selectedRasterLayer.udm,
          UDM_CONVERSIONS: this.selectedRasterLayer.udm_conversions,
          IS_MULTI_VARIABLE: this.selectedRasterLayer.is_multi_variable,
          MULTI_VARIABLE_LAYERS: this.selectedRasterLayer.multi_variable_layers || [],
          MULTI_VARIABLE_LAYER_CONFIGS: this.selectedRasterLayer.multi_variable_layer_configs || [],
          EXTENT: extent
        };

        await this.loadRasterLayer(rasterParams);
      } else {
        this.$emit('no-vector-features-found', this.animationEmptySteps.includes(this.animationTimeArray[this.index]));
        await this.openVectorLayer();
      }
    },
    async openVectorLayer() {
      this.map.sliderOpacityValue = this.selectedVectorLayer.layer_opacity;

      const vectorParams = {
        params: {
          GROUP: this.$route.params.id,
          LAYERS: `${this.selectedVectorLayer.ws_name}:${this.selectedVectorLayer.layer_name}`,
          TIME: this.animationTimeArray[this.index],
          STYLES: this.selectedVectorLayer.layer_style,
          USERNAME: this.app_user.username,
          SERVICE_NAME: this.serviceName,
          PARAM_RANGE: this.map.vectorParamRange[this.serviceCardId]
        },
        TYPE: 'VECTOR',
        LAYER_SERVICE_ID: this.serviceCardId,
        UDM: this.selectedVectorLayer.udm,
        UDM_CONVERSIONS: this.selectedVectorLayer.udm_conversions,
        EXTENT: this.geoserver_data.geographicDomains[this.serviceCardId],
        AGGREGATION_INTERVAL: this.selectedVectorLayer.aggregation_interval ?? null
      };

      const layerFound = this.map.OLmap.getLayers().getArray().slice(1).some(selectedLayer => selectedLayer.values_.time === vectorParams.params.TIME && (selectedLayer.values_.layer_name === vectorParams.params.LAYERS && selectedLayer.values_.service_id === vectorParams.LAYER_SERVICE_ID));

      if (!layerFound) {
        await this.updateVectorLayer(vectorParams);
      }
    },
    isLandscape() {
      return /Mobi|Android/i.test(navigator.userAgent) && window.innerHeight < window.innerWidth;
    },
    mobileBreakpoints() {
      return /Mobi|Android/i.test(navigator.userAgent) || this.isLandscape();
    }
  }
};
</script>

<style scoped>
.RMprimary-color {
  color: #00A644;
}
@media (min-device-width:280px) and (max-device-width:767px){
  .mobile-margin {
   padding-left: 12px !important;
  }
}
</style>
